<template>
    <div class="credit-card">
        <billing-pattern class="pattern" />

        <div class="card-header">
            <div class="logo">
                <CardLogo />
            </div>
            <div class="card-amount">
                <span>{{ formatCurrency(creditAmount) }}</span>
            </div>
        </div>

        <div class="card-body">
            <div class="card-info">
                <div class="card-name">{{ truncateText(email.toUpperCase(), 30) }}</div>
                <div class="card-number">{{ code }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import CardLogo from '@/assets/images/branding/card-logo.svg';
import BillingPattern from '@/assets/images/illustrations/billing-pattern.svg';
import { formatCurrency } from '@privacy-bee/format/currency';

const { truncateText } = useUtils();

interface Props {
    creditAmount: number;
    email: string;
    /** The gift-certificate code exactly as app-api wrote it, shown character for character. */
    code: string;
}

defineProps<Props>();
</script>

<style scoped lang="scss">
.pattern {
    position: absolute;
    left: 0;
    bottom: 0;
}

.credit-card {
    @apply w-full lg:w-80 h-44 py-4 px-6 text-ink-invert relative overflow-hidden rounded-2xl border border-white;
    @apply flex justify-between flex-col;
    /* A fixed dark card plate: it carries --ink-invert text in both themes, so the two
       stops do not theme and no surface token names them. */
    background: linear-gradient(45deg, #181d27 0%, #414651 100%);
}

.card-header {
    @apply flex justify-between relative z-[1];
}

.logo {
    @apply flex items-center gap-2;
}

.card-body {
    @apply flex justify-between items-end relative z-[1];
}

.card-info {
    @apply flex flex-col gap-2;
}

.card-name {
    @apply text-ink-invert text-xs not-italic font-medium leading-normal uppercase tracking-[0.6px];
}

// The code is a string the member copies, so it is set in the machine face. Forty characters are
// wider than the card, so it wraps onto a second line rather than running off the plate.
.card-number {
    @apply text-ink-invert not-italic;
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
    font-weight: var(--weight-medium);
    overflow-wrap: anywhere;
}

.card-amount {
    @apply rounded inline-flex py-2 px-2.5 justify-center items-center gap-2.5 text-zinc-50 text-right text-sm not-italic;
    @apply font-bold leading-normal tracking-[0.7px];
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: rgba(9, 9, 11, 0.6);
}
</style>
